import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ActiveTimer, TimeEntry, CreateTimeEntryRequest } from '../../types';
import { useAuth } from './AuthContext';
import { useRBAC } from './RBACContext';
import apiService from '../../utils/api.service';

interface TimeTrackingContextType {
  activeTimer: ActiveTimer | null;
  todayEntries: TimeEntry[];
  weekEntries: TimeEntry[];
  isLoading: boolean;
  startTimer: (data: { projectId?: string; taskId?: string; description?: string }) => Promise<void>;
  stopTimer: () => Promise<void>;
  discardTimer: () => Promise<void>;
  fetchTodayEntries: () => Promise<void>;
  fetchWeekEntries: (date?: string) => Promise<void>;
  createEntry: (data: CreateTimeEntryRequest) => Promise<void>;
  updateEntry: (id: string, data: Partial<TimeEntry>) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
}

const TimeTrackingContext = createContext<TimeTrackingContextType | undefined>(undefined);

export const useTimeTracking = () => {
  const context = useContext(TimeTrackingContext);
  if (!context) throw new Error('useTimeTracking must be used within TimeTrackingContextProvider');
  return context;
};

const getISOMonday = (date: Date): string => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
};

export const TimeTrackingContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { isSuperAdmin } = useRBAC();
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [todayEntries, setTodayEntries] = useState<TimeEntry[]>([]);
  const [weekEntries, setWeekEntries] = useState<TimeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>();

  const fetchTodayEntries = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await apiService.getDayEntries(today);
      const data = response.data || response;
      const entries = Array.isArray(data) ? data : (data?.entries || []);
      setTodayEntries(entries);
    } catch {
      // Silently handle - entries remain as previous state
    }
  }, []);

  const fetchWeekEntries = useCallback(async (date?: string) => {
    try {
      const monday = date || getISOMonday(new Date());
      const response = await apiService.getWeekEntries(monday);
      const data = response.data || response;
      const entries = Array.isArray(data) ? data : (data?.entries || []);
      setWeekEntries(entries);
    } catch {
      // Silently handle - entries remain as previous state
    }
  }, []);

  const fetchCurrentTimer = useCallback(async () => {
    try {
      const response = await apiService.getCurrentTimer();
      const data = response.data || response;
      if (data && data.entry) {
        setActiveTimer(data);
      } else {
        setActiveTimer(null);
      }
    } catch {
      setActiveTimer(null);
    }
  }, []);

  // Timer ticking: increment elapsedSeconds every second when a timer is active
  useEffect(() => {
    if (activeTimer?.entry?.timerStartedAt) {
      const update = () => {
        const start = new Date(activeTimer.entry.timerStartedAt!).getTime();
        const elapsed = Math.floor((Date.now() - start) / 1000);
        setActiveTimer(prev => prev ? { ...prev, elapsedSeconds: elapsed } : null);
      };
      update();
      timerRef.current = setInterval(update, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [activeTimer?.entry?.timerStartedAt]);

  // Load timer and today entries on auth (skip for super admin - no org context)
  useEffect(() => {
    if (isAuthenticated && !isSuperAdmin) {
      setIsLoading(true);
      Promise.all([fetchCurrentTimer(), fetchTodayEntries()])
        .finally(() => setIsLoading(false));
    } else {
      setActiveTimer(null);
      setTodayEntries([]);
      setWeekEntries([]);
    }
  }, [isAuthenticated, isSuperAdmin, fetchCurrentTimer, fetchTodayEntries]);

  const startTimer = useCallback(async (data: { projectId?: string; taskId?: string; description?: string }) => {
    try {
      const response = await apiService.startTimer(data);
      const timerData = response.data || response;
      setActiveTimer(timerData);
    } catch (err: any) {
      throw err;
    }
  }, []);

  const stopTimer = useCallback(async () => {
    try {
      await apiService.stopTimer();
      setActiveTimer(null);
      clearInterval(timerRef.current);
      await fetchTodayEntries();
    } catch (err: any) {
      throw err;
    }
  }, [fetchTodayEntries]);

  const discardTimer = useCallback(async () => {
    try {
      await apiService.discardTimer();
      setActiveTimer(null);
      clearInterval(timerRef.current);
    } catch (err: any) {
      throw err;
    }
  }, []);

  const createEntry = useCallback(async (data: CreateTimeEntryRequest) => {
    try {
      await apiService.createTimeEntry(data);
      await fetchTodayEntries();
    } catch (err: any) {
      throw err;
    }
  }, [fetchTodayEntries]);

  const updateEntry = useCallback(async (id: string, data: Partial<TimeEntry>) => {
    try {
      await apiService.updateTimeEntry(id, data);
      await fetchTodayEntries();
    } catch (err: any) {
      throw err;
    }
  }, [fetchTodayEntries]);

  const deleteEntry = useCallback(async (id: string) => {
    try {
      await apiService.deleteTimeEntry(id);
      await fetchTodayEntries();
    } catch (err: any) {
      throw err;
    }
  }, [fetchTodayEntries]);

  return (
    <TimeTrackingContext.Provider value={{
      activeTimer, todayEntries, weekEntries, isLoading,
      startTimer, stopTimer, discardTimer,
      fetchTodayEntries, fetchWeekEntries,
      createEntry, updateEntry, deleteEntry,
    }}>
      {children}
    </TimeTrackingContext.Provider>
  );
};
